'use client';

import { useEffect, useState } from 'react';
import { getPeople } from '@/lib/actions/people';
import { Link } from '@/i18n/routing';
import {
  Plus,
  Pencil,
  User as UserIcon,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DeletePersonButton } from '@/components/admin/people/DeletePersonButton';
import { MediaImg } from '@/components/shared/MediaImg';

export default function AdminPeoplePage() {
  const [people, setPeople] = useState<Awaited<ReturnType<typeof getPeople>>>([]);

  useEffect(() => {
    getPeople().then(setPeople);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy">Manage Our People</h1>
          <p className="text-muted-foreground mt-1">Add and manage team members.</p>
        </div>
        <Button asChild className="bg-primary hover:bg-navy text-primary-foreground font-semibold">
          <Link href="/admin/people/new">
            <Plus className="mr-2 h-4 w-4" /> Add Person
          </Link>
        </Button>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[80px]">Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Position</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {people.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12 text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <UserIcon size={40} className="text-gray-300" />
                    <p>No team members found. Add your first member!</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              people.map((person: any) => (
                <TableRow key={person.id}>
                  <TableCell className="font-medium">{person.order}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {person.image ? (
                        <MediaImg
                          src={person.image}
                          alt={person.name}
                          className="h-10 w-10 rounded-full object-cover border"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 border">
                          <UserIcon size={18} />
                        </div>
                      )}
                      <span className="font-semibold">{person.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{person.position}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" asChild title="View on Site">
                        <Link href="/about-us#our-team" target="_blank">
                          <ExternalLink className="h-4 w-4 text-gray-500" />
                        </Link>
                      </Button>
                      <Button variant="ghost" size="icon" asChild title="Edit">
                        <Link href={`/admin/people/${person.id}`}>
                          <Pencil className="h-4 w-4 text-blue-600" />
                        </Link>
                      </Button>
                      <DeletePersonButton id={person.id} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
